import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { StorageUtils, textFormatter } from 'src/utils';
import {
  assignConfirmModalId,
  assignModalId,
  assignSubMenuModalId,
  resetConfirmModalId,
  toggleNavBar
} from 'src/store/common/common.slice';
import { useGetIdentity, useSignOut } from 'src/hooks';

import InviteUser from './inviteUser/InviteUser';
import './header.scss';

import {
  HamburgerIcon,
  NotificationIcon,
  PlusIcon,
  SingleChevronIcon
} from '../common/common.icons';
import ProfilePicture from '../profile/profilePicture/ProfilePicture';
import Typography from '../common/typography/Typography';
import SubMenuModal from '../common/subMenuModal/SubMenuModal';
import { ConfirmModal, Dropdown, TDropdown } from '../common';
import { SearchBar } from '../search';
import Button from '../button/Button';
import { inviteUserModalId } from '../cards/inviteNetwork';
import { hTractionLogoWithText } from 'src/assets/images';
import { socketService } from 'src/services/socket.api.service';
import { SocketAddress } from 'net';
import { setNotificationCount } from 'src/store/chat/chat.slice';

const signOutModalId = 'sign-up-modal-id';

const Header = () => {
  const { profile } = useAppSelector((store) => store.profile);
  const { companyProfilePic, myCompany } = useAppSelector((store) => store.company);
  const { dropdownId, showNavBar } = useAppSelector((store) => store.common);
  const { highlightNotificationBell, ideaHubNotificationCount } = useAppSelector(
    (store) => store.notifications
  );

  // Constants
  const hasNotifications = ideaHubNotificationCount > 0 || highlightNotificationBell;

  // Hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthorized } = useAppSelector((store) => store.auth);
  const { notificationCount } = useAppSelector((store) => store.chat);
  const { getIdentities } = useGetIdentity();

  // state
  const menuRef = useRef(null);
  const openSignOutModal = () => {
    dispatch(assignConfirmModalId(signOutModalId));
  };

  const handleNavBar = () => {
    dispatch(toggleNavBar());
  };

  const isCompanyCreated = Boolean(StorageUtils.get('companyId'));
  const { loggedInUserType, authenticatedId } = getIdentities();
  const headerProfilePic = loggedInUserType === 'User' ? profile?.profilePic : companyProfilePic;
  const headerName = loggedInUserType === 'User' ? profile?.name : myCompany.name;
  const includedPath = ['/access-denied', '/success', '/keymember-verification', '/apply-to-program-external', '/apply-to-event-external', '/feedback', '/check-password', '/application-rating', '/apply-to-program', '/survey-report']
   const profilePictureUrl = useMemo(() => {
    return headerProfilePic ? `${headerProfilePic}?t=${Date.now()}` : '';
  }, [headerProfilePic]);
  const navigateCompany = () => {
    if (isCompanyCreated) {
      navigate(`/my-company?tab=about&prev=${window.location.pathname}`);
    } else {
      navigate(`/create-company?prev=${window.location.pathname}`);
    }
  };

  const openNotifications = () => {
    navigate('/notifications?tab=all');
    dispatch(setNotificationCount(0));
  };

  const navigateToSettings = () => {
    navigate('/settings');
  };
  const navigateToDashboard = () => {
    const path = isAuthorized ? '/dashboard' : '/';
    navigate(path)
  }

  const headerDropdownItems = useMemo(() => {
    const items = [
      {
        label: isCompanyCreated ? 'Company Profile' : 'Create Company Page',
        onClick: navigateCompany
      },

      { label: 'Settings', onClick: navigateToSettings },
      { label: 'Logout', onClick: openSignOutModal }
    ];

    if (loggedInUserType === 'User') {
      items.unshift({ label: 'My Profile', onClick: () => navigate('/my-profile') });
    }

    return items;
  }, [isCompanyCreated]);

  const headerOptions: TDropdown = {
    id: 'header-dropdown',
    size: 'large',
    alignment: 'left',
    items: headerDropdownItems
  };
  const openDropdown = () => {
    if (dropdownId) {
      dispatch(assignSubMenuModalId(''));
    } else {
      dispatch(assignSubMenuModalId(headerOptions.id));
    }
  };

  const handleOpenInviteUserModal = () => {
    dispatch(assignModalId(inviteUserModalId));
  };
  // Detect Click Outside & Close Navbar
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (!showNavBar || !menuRef.current) return;

      const navigationMenu = document.querySelector('.navigation__menu'); // Selects the menu
      // Check if the click is inside menuRef or navigation__menu
      if (
        menuRef.current.contains(event.target) ||
        navigationMenu?.contains(event.target)
      ) {
        return; // Do nothing if clicking inside
      }

      handleNavBar(); // Close sidebar when clicking outside
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNavBar]);

  // useEffect(() => {
  //   if (authenticatedId && loggedInUserType) {
  //     try {
  //       socketService.connect();
  //       socketService.on("connect", () => {
  //         socketService.emit("join", { userId: authenticatedId });
  //         socketService.on("joined-room", (data) => {
  //           console.log("Joined confirmation:", data);
  //         });

  //         socketService.emit("notifications-unread", {
  //           userId: authenticatedId,
  //           userType: loggedInUserType
  //         });

  //         const handleNotificationUnread = (data) => {
  //           dispatch(setNotificationCount(data.count))
  //         };

  //         socketService.on("notifications-unread", handleNotificationUnread);

  //         const handleIdeaConversation = (data) => {
  //           console.log(`Received message in ${authenticatedId}:`, data);
  //         };

  //         socketService.on("idea-conversation", handleIdeaConversation);

  //         socketService.on("connect_error", (err) => {
  //           console.error("Connection Error:", err);
  //         });

  //         // Cleanup when component unmounts
  //         return () => {
  //           socketService.off("notifications-unread", handleNotificationUnread);
  //           socketService.off("idea-conversation", handleIdeaConversation);
  //           socketService.off("connect_error");
  //           socketService.disconnect();
  //         };
  //       });
  //     } catch (err) {
  //       console.log(err, "errorData");
  //     }
  //   }
  // }, [authenticatedId, loggedInUserType]);

  return (
    <div className={isAuthorized ? `header-component ${includedPath.includes(location.pathname) ? 'header-component__full-page' : ''}` : 'header-component header-component__without-login'} >
      {includedPath.includes(location.pathname) && <img
        src={hTractionLogoWithText}
        alt="htraction logo"
        className="header-component__htraction-logo"
        onClick={navigateToDashboard}
      />}
      {isAuthorized && <div className="header-component__search">
        {/* <SearchBar /> */}
      </div>}
      {!isAuthorized && <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <div className="header-component__search" style={{ opacity: '0', width: '85%' }}></div>
        <Button
          variant="secondary"
          size="extraSmall"
          style={{ width: 'auto' }}
          className="header-component__right__invite-user"
          onClick={() => navigate('/login')}
        >
          Login / Signup
        </Button>
      </div>}
      {<div className="header-component__right">
        {isAuthorized && <Button
          variant="secondary"
          size="extraSmall"
          startIcon={<PlusIcon />}
          className="header-component__right__invite-user"
          onClick={handleOpenInviteUserModal}
        >
          Invite User
        </Button>}
        {isAuthorized && <div className="header-component__right__notifications" onClick={openNotifications}>
          <div className="header-component__right__notifications__container">
            {notificationCount > 0 && (
              <div className="header-component__right__notifications__container__new-notification">
                <span>{notificationCount > 99 ? `${99}+` : notificationCount}</span>
              </div>
            )}
            <NotificationIcon height='22px' width='24px' />
          </div>
          <div className='header-component__right__notifications__search'>
            <SearchBar placeholder='Search'/>

          </div>
        </div>}
        <div className="header-component__right__menu" >
          {isAuthorized && <>
            <div className="header-component__right__menu__profile-picture" onClick={(e) => {
              e.stopPropagation();
              openDropdown();
            }}>
              <ProfilePicture
                url={profilePictureUrl || ''}
                fallback={textFormatter({ value: headerName[0] || '' })}
                userType={loggedInUserType}
              />
            </div>
            <Typography as="p" variant="body-2" fontWeight="medium" className='cursor_pointer' onClick={(e) => {
              e.stopPropagation();
              openDropdown();
            }}>
            </Typography>
            <div
              className="header-component__right__menu__arrow"
              onClick={(e) => {
                e.stopPropagation();
                openDropdown();
              }}
            >
              <SingleChevronIcon direction="down" />
            </div>
          </>}
          {!includedPath.includes(location.pathname) && <div className="header-component__open-navigation" onClick={handleNavBar} ref={menuRef}>
            <HamburgerIcon />
          </div>}
          <SubMenuModal>
            <Dropdown {...headerOptions} />
          </SubMenuModal>
        </div>
      </div>}

      {/* {openInviteUserModal && isAuthorized && (
        <InviteUser isModalOpen={openInviteUserModal} closeModal={handleCloseInviteUserModal} />
      )} */}
    </div >
  );
};

export default Header;
